/* ==========================================================================
   Horizon - Panel de Zoom lateral (Desktop) + Zoom por clic (Mobile)
   Archivo: assets/hz-zoom-panel.js
   Requiere:
   - En Liquid: wrapper [data-hz-media-zoom] y panel [data-hz-zoom-panel]
   - En CSS: hz-zoom-panel.css (panel solo visible en desktop)
   ========================================================================== */

(() => {
  // Puede haber más de un bloque en la página (quick view, secciones, etc.)
  const roots = Array.from(document.querySelectorAll('[data-hz-media-zoom]'));
  if (!roots.length) return;

  // Helpers
  const isElementVisible = (el) => {
    if (!el) return false;
    if (el.closest('dialog')) return false; // no queremos imágenes del modal
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 10 && rect.height > 10;
  };

  // Toma la URL más grande posible desde srcset (mejor nitidez para el zoom)
  const pickLargestFromSrcset = (img) => {
    if (!img) return null;
    const srcset = img.getAttribute('srcset');
    if (!srcset) return img.currentSrc || img.src || null;

    const candidates = srcset
      .split(',')
      .map((s) => s.trim().split(/\s+/))
      .filter((parts) => parts[0])
      .map((parts) => ({
        url: parts[0],
        w: parseInt((parts[1] || '').replace('w', ''), 10) || 0,
      }))
      .sort((a, b) => a.w - b.w);

    return candidates.length ? candidates[candidates.length - 1].url : (img.currentSrc || img.src || null);
  };

  // Busca la "imagen activa" en la galería visible (NO en el dialog)
  const getActiveImage = (mediaGallery) => {
    if (!mediaGallery) return null;

    // 1) Preferir la imagen dentro del slide activo del carousel
    const activeSlide = mediaGallery.querySelector('slideshow-slide[aria-hidden="false"]');
    if (activeSlide) {
      const imgs = Array.from(activeSlide.querySelectorAll('img'))
        .filter((img) => !img.closest('.dialog-zoomed-gallery') && !img.closest('.dialog-thumbnails-list') && isElementVisible(img));
      if (imgs.length) return imgs[0];
    }

    // 2) Si es grid, o si no detectó el slide: buscar la imagen más "grande" visible
    const allImgs = Array.from(mediaGallery.querySelectorAll('img'))
      .filter((img) => !img.closest('.dialog-zoomed-gallery'))
      .filter((img) => !img.closest('.dialog-thumbnails-list'))
      .filter((img) => isElementVisible(img));

    if (!allImgs.length) return null;

    // Elegimos la de mayor área visible (normalmente es la principal)
    let best = null;
    let bestScore = 0;

    for (const img of allImgs) {
      const r = img.getBoundingClientRect();
      const score = r.width * r.height;
      if (score > bestScore) {
        bestScore = score;
        best = img;
      }
    }

    return best;
  };

  // Encuentra el "contenedor clicable" que Horizon usa para abrir zoom (si existe)
  const findZoomClickTarget = (img, mediaGallery) => {
    if (!img || !mediaGallery) return null;

    // En carousel: Horizon suele poner el handler en slideshow-slide (on:click="#zoom-dialog...")
    const slide = img.closest('slideshow-slide');
    if (slide) return slide;

    // En grid: existe un botón overlay .product-media-container__zoom-button dentro del li
    const gridItem = img.closest('li.product-media-container, .product-media-container');
    if (gridItem) {
      const btn = gridItem.querySelector('.product-media-container__zoom-button');
      if (btn) return btn;
      return gridItem;
    }

    return img;
  };

  // Activa el panel: background-image + clase active
  const setZoomPanelImage = (zoomPanel, img) => {
    if (!zoomPanel) return;
    if (!img) {
      zoomPanel.classList.remove('is-active');
      zoomPanel.style.backgroundImage = '';
      return;
    }

    const url = pickLargestFromSrcset(img);
    if (!url) {
      zoomPanel.classList.remove('is-active');
      zoomPanel.style.backgroundImage = '';
      return;
    }

    zoomPanel.style.backgroundImage = `url("${url}")`;
  };

  // Actualiza posición del "zoom" según mouse
  const updateZoomPosition = (zoomPanel, img, clientX, clientY) => {
    if (!zoomPanel || !img) return;

    const rect = img.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;

    const bx = Math.max(0, Math.min(1, x)) * 100;
    const by = Math.max(0, Math.min(1, y)) * 100;

    zoomPanel.style.backgroundPosition = `${bx}% ${by}%`;
  };

  // Detecta "desktop real" (hover + puntero fino + ancho)
  const isDesktopMode = () =>
    window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)').matches;

  // Inicializa cada bloque/instancia
  roots.forEach((root) => {
    const zoomPanel = root.querySelector('[data-hz-zoom-panel]');
    const mediaGallery = root.querySelector('media-gallery');

    if (!mediaGallery) return;

    // Para evitar duplicar listeners en re-renders del theme editor
    if (root.__hzZoomInitialized) return;
    root.__hzZoomInitialized = true;

    // ---- Desktop: panel lateral ----
    const setupDesktop = () => {
      if (!zoomPanel) return;

      const refresh = () => {
        const img = getActiveImage(mediaGallery);

        // Si el media activo NO es imagen (video/model), ocultar panel
        if (!img) {
          zoomPanel.classList.remove('is-active');
          zoomPanel.style.backgroundImage = '';
          return;
        }

        setZoomPanelImage(zoomPanel, img);
      };

      refresh();

      // Mouse interactions
      let currentImg = null;

      const onEnter = () => {
        currentImg = getActiveImage(mediaGallery);
        if (!currentImg) return;

        setZoomPanelImage(zoomPanel, currentImg);
        zoomPanel.classList.add('is-active');
      };

      const onMove = (e) => {
        if (!zoomPanel.classList.contains('is-active')) return;
        if (!currentImg || !isElementVisible(currentImg)) currentImg = getActiveImage(mediaGallery);
        if (!currentImg) return;

        updateZoomPosition(zoomPanel, currentImg, e.clientX, e.clientY);
      };

      const onLeave = () => {
        zoomPanel.classList.remove('is-active');
      };

      // Delegamos sobre el root para que funcione aunque cambie el DOM
      root.addEventListener('mouseenter', (e) => {
        const img = e.target.closest('img');
        if (!img) return;
        // Solo si es una imagen de la galería principal (no thumbnails, no dialog)
        if (img.closest('.dialog-zoomed-gallery') || img.closest('.dialog-thumbnails-list')) return;
        onEnter();
      }, true);

      root.addEventListener('mousemove', (e) => {
        const img = e.target.closest('img');
        if (!img) return;
        if (img.closest('.dialog-zoomed-gallery') || img.closest('.dialog-thumbnails-list')) return;
        onMove(e);
      }, true);

      root.addEventListener('mouseleave', () => onLeave(), true);

      // Observer: si cambia la imagen activa por flechas/controls, refrescamos el fondo
      const obs = new MutationObserver(() => refresh());
      obs.observe(mediaGallery, { subtree: true, childList: true, attributes: true });

      root.__hzZoomObserver = obs;
    };

    // ---- Mobile/Tablet: zoom solo al click ----
    const setupMobile = () => {
      // Solo tiene sentido si Horizon tiene zoom-dialog habilitado
      const hasZoomDialog = !!mediaGallery.querySelector('zoom-dialog');
      if (!hasZoomDialog) return;

      root.addEventListener('click', (e) => {
        const img = e.target.closest('img');
        if (!img) return;

        // Evitar thumbnails del dialog
        if (img.closest('.dialog-zoomed-gallery') || img.closest('.dialog-thumbnails-list')) return;

        // Solo si la imagen es parte de la galería principal
        if (!img.closest('media-gallery')) return;

        // Disparar el mismo target que usa Horizon para abrir zoom
        const target = findZoomClickTarget(img, mediaGallery);
        if (!target) return;

        // Importante: no bloqueamos el click normal si el usuario clicó un botón ya existente
        // pero sí intentamos activar zoom al tocar la imagen
        try {
          target.click();
        } catch (_) {
          // Fallback: no hacer nada
        }
      }, true);
    };

    // Setup inicial según modo
    const initForCurrentMode = () => {
      // Limpieza previa
      if (root.__hzZoomObserver) {
        root.__hzZoomObserver.disconnect();
        root.__hzZoomObserver = null;
      }
      if (zoomPanel) {
        zoomPanel.classList.remove('is-active');
      }

      if (isDesktopMode()) setupDesktop();
      else setupMobile();
    };

    initForCurrentMode();

    // Si cambia el viewport (rotate, resize), reiniciar modo
    const onResize = () => {
      // debounce simple
      clearTimeout(root.__hzZoomResizeT);
      root.__hzZoomResizeT = setTimeout(() => initForCurrentMode(), 150);
    };
    window.addEventListener('resize', onResize);
  });
})();